// v2.20.2 · suscripción a la cola RECIENTE del canal rápido por sesión/secuencia. Es el mismo
// contrato de v2.20.1 (ceros, huecos y tiempos reales se conservan): aquí sólo se reutiliza para
// la curva grande de la ficha y para las mini curvas del mosaico.
//
// ID4 · UNA sola lectura por cama. La tarjeta del mosaico y la ficha abierta de la MISMA cama
// comparten cursor y cola: el registro consulta al ritmo del suscriptor activo más rápido y entrega
// a cada uno su propia ventana. Camas distintas nunca comparten entrada. Sin suscriptores activos
// (pestaña oculta, mosaico plegado) no se consulta; al reactivarse, la primera consulta es inmediata.
//
// ID4 R02 · la cola y el cursor se REINICIAN (nada viejo se muestra mientras llega lo nuevo):
//  - cuando la cama se queda sin suscriptores activos (pausa, ficha cerrada sin tarjeta activa);
//  - cuando cambia la identidad del equipo/periodo, declarada por quien se suscribe (fila del
//    mosaico) o por el propio servidor en la respuesta (`device_id` + `asignacion`);
//  - con 403/404 se descarta lo acumulado.
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// La cola compartida guarda al menos la ventana de la curva grande; cada suscriptor recorta la suya.
const VENTANA_MINIMA = 256;
const registro = new Map();

function vistaPara(vivo, ventana) {
  if (!vivo) return null;
  return {
    ...vivo,
    heart: vivo.heart.slice(-ventana),
    resp: vivo.resp.slice(-ventana),
    t: vivo.t.slice(-ventana),
  };
}

function identidadServidor(data) {
  if (!data || !data.asignacion) return null;
  return [data.device_id ?? "", data.asignacion.id ?? "", data.asignacion.version ?? ""].join("|");
}

// R03 · cada entrega a un suscriptor lleva, en la MISMA actualización, la identidad que declaró el
// servidor (`device_id|asignacion.id|asignacion.version`) para la cola que entrega. Así quien dibuja
// otra cosa de esa cama (el respaldo de la ficha) sabe en el mismo render si sigue siendo del periodo.
function estadoPara(entrada, sub) {
  return { vivo: vistaPara(entrada.vivo, sub.ventana), sinAcceso: entrada.sinAcceso,
    identidad: entrada.identidadServidor };
}

function notificar(entrada) {
  for (const sub of entrada.subs) {
    if (!sub.activo) continue;
    sub.set(estadoPara(entrada, sub));
  }
  // El veto de acceso se comunica también a quien no está activo: es un veredicto de alcance.
  if (entrada.sinAcceso) {
    for (const sub of entrada.subs) if (!sub.activo) sub.set({ vivo: null, sinAcceso: true, identidad: null });
  }
}

function reiniciar(entrada) {
  // Cola y cursor vuelven a cero; una respuesta en vuelo de la generación anterior se descarta.
  entrada.cursor = { session: null, seq: null };
  entrada.vivo = null;
  entrada.identidadServidor = null;
  entrada.generacion += 1;
  entrada.enVuelo = false;
}

async function consultar(entrada) {
  // Cerca de concurrencia: con el ciclo anterior en vuelo no se dispara otro con el mismo cursor
  // (dos respuestas del mismo rango duplicarían muestras).
  if (entrada.enVuelo || entrada.cerrada || entrada.sinAcceso) return;
  entrada.enVuelo = true;
  const generacion = entrada.generacion;
  const camaId = entrada.camaId;
  try {
    const params = {};
    if (entrada.cursor.session) {
      params.session_id = entrada.cursor.session;
      if (entrada.cursor.seq != null) params.desde_seq = entrada.cursor.seq;
    }
    const { data } = await api.get(`/ekh/vista/camas/${camaId}/curva-viva`, { params });
    if (entrada.cerrada || generacion !== entrada.generacion) return;
    if (data?.cama_id && data.cama_id !== camaId) return;
    const ident = identidadServidor(data);
    const otraIdentidad = Boolean(ident && entrada.identidadServidor && ident !== entrada.identidadServidor);
    if (ident) entrada.identidadServidor = ident;
    const h = data?.muestras?.heart_amplitude || [];
    const r = data?.muestras?.respiratory_amplitude || [];
    const ts = data?.muestras?.t || [];
    if (otraIdentidad && !data?.nueva_sesion) {
      // Cambió el equipo o el periodo y la respuesta viene del cursor anterior: no se mezcla. Se
      // vacía y la consulta siguiente se ancla de nuevo al periodo vigente.
      entrada.cursor = { session: null, seq: null };
      entrada.vivo = null;
      notificar(entrada);
      return;
    }
    const reinicia = otraIdentidad || Boolean(data?.nueva_sesion) ||
      (data?.session_id && data.session_id !== entrada.cursor.session);
    entrada.cursor = {
      session: data?.session_id || entrada.cursor.session,
      seq: data?.ultimo_seq ?? entrada.cursor.seq,
    };
    const prev = entrada.vivo;
    const base = reinicia || !prev
      ? { heart: [], resp: [], t: [], huecos: [], recibidas: 0 } : prev;
    // Un hueco DECLARADO entre el tramo anterior y el nuevo se dibuja como corte (null).
    const corte = (!reinicia && base.heart.length && (data?.huecos || []).length) ? [null] : [];
    const ventana = entrada.ventana;
    entrada.vivo = {
      session_id: entrada.cursor.session,
      ultimo_seq: entrada.cursor.seq,
      cadencia_ms: data?.cadencia_ms ?? base.cadencia_ms ?? null,
      heart: [...base.heart, ...corte, ...h].slice(-ventana),
      resp: [...base.resp, ...corte, ...r].slice(-ventana),
      t: [...base.t, ...ts].slice(-ventana),
      huecos: [...(base.huecos || []), ...(data?.huecos || [])].slice(-20),
      recibidas: (base.recibidas || 0) + h.length,
      razon: data?.lectura?.razon || null,
    };
    notificar(entrada);
  } catch (e) {
    const st = e?.response?.status;
    if ((st === 403 || st === 404) && !entrada.cerrada && generacion === entrada.generacion) {
      // M3 · sin acceso: nada de lo acumulado se sigue mostrando.
      entrada.sinAcceso = true;
      entrada.vivo = null;
      programar(entrada);
      notificar(entrada);
    }
  } finally {
    if (generacion === entrada.generacion) entrada.enVuelo = false;
  }
}

function programar(entrada) {
  const activos = [...entrada.subs].filter((s) => s.activo);
  const intervalo = activos.length && !entrada.sinAcceso && !entrada.cerrada
    ? Math.min(...activos.map((s) => s.intervaloMs)) : null;
  entrada.ventana = Math.max(VENTANA_MINIMA, ...[...entrada.subs].map((s) => s.ventana));
  if (!activos.length && (entrada.vivo || entrada.cursor.session)) {
    // H2 · sin nadie mirando, la cola se olvida: al reactivarse se ancla de nuevo.
    reiniciar(entrada);
  }
  if (intervalo === entrada.intervalo) return;
  const estabaDetenida = entrada.intervalo == null;
  if (entrada.timer) clearInterval(entrada.timer);
  entrada.timer = null;
  entrada.intervalo = intervalo;
  if (intervalo == null) return;
  entrada.timer = setInterval(() => consultar(entrada), intervalo);
  // Al activarse (apertura, pestaña visible, mosaico desplegado) la primera lectura es inmediata.
  if (estabaDetenida) consultar(entrada);
}

function aplicarIdentidad(entrada, sub) {
  // Identidad declarada por el suscriptor (equipo y periodo de la fila del mosaico). Si cambia,
  // lo acumulado pertenece a otro periodo: se reinicia y todos los suscriptores se vacían.
  if (!sub.identidad) return;
  if (entrada.identidad && entrada.identidad !== sub.identidad) {
    reiniciar(entrada);
    entrada.identidad = sub.identidad;
    for (const s of entrada.subs) s.set({ vivo: null, sinAcceso: entrada.sinAcceso, identidad: null });
    if (entrada.intervalo != null) consultar(entrada);
    return;
  }
  entrada.identidad = sub.identidad;
}

function suscribir(camaId, sub) {
  let entrada = registro.get(camaId);
  if (!entrada) {
    entrada = { camaId, subs: new Set(), cursor: { session: null, seq: null }, vivo: null,
      sinAcceso: false, enVuelo: false, timer: null, intervalo: null, ventana: VENTANA_MINIMA,
      cerrada: false, generacion: 0, identidad: null, identidadServidor: null };
    registro.set(camaId, entrada);
  }
  entrada.subs.add(sub);
  aplicarIdentidad(entrada, sub);
  // Quien se suma a una cama ya consultada recibe de inmediato lo acumulado (sin otra lectura).
  if (sub.activo && entrada.vivo) sub.set(estadoPara(entrada, sub));
  else if (entrada.sinAcceso) sub.set({ vivo: null, sinAcceso: true, identidad: null });
  programar(entrada);
  return entrada;
}

function desuscribir(entrada, sub) {
  entrada.subs.delete(sub);
  if (!entrada.subs.size) {
    // Sin suscriptores la cama se olvida: una apertura posterior arranca limpia, como antes.
    entrada.cerrada = true;
    if (entrada.timer) clearInterval(entrada.timer);
    entrada.timer = null;
    if (registro.get(entrada.camaId) === entrada) registro.delete(entrada.camaId);
    return;
  }
  programar(entrada);
}

export default function useCurvaViva(camaId, {
  activo = true, intervaloMs = 1000, ventana = 256, identidad = null } = {}) {
  const [estado, setEstado] = useState({ vivo: null, sinAcceso: false, identidad: null });
  const subRef = useRef(null);
  const entradaRef = useRef(null);

  useEffect(() => {
    // Al cambiar de cama no se arrastra nada de la anterior.
    setEstado({ vivo: null, sinAcceso: false, identidad: null });
    if (!camaId) return undefined;
    const sub = { activo, intervaloMs, ventana, identidad, set: setEstado };
    subRef.current = sub;
    entradaRef.current = suscribir(camaId, sub);
    return () => {
      desuscribir(entradaRef.current, sub);
      subRef.current = null;
      entradaRef.current = null;
    };
    // Los parámetros se actualizan en el efecto siguiente sin rehacer la suscripción.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camaId]);

  useEffect(() => {
    const sub = subRef.current;
    const entrada = entradaRef.current;
    if (!sub || !entrada) return;
    const seActiva = activo && !sub.activo;
    sub.activo = activo;
    sub.intervaloMs = intervaloMs;
    sub.ventana = ventana;
    sub.identidad = identidad;
    aplicarIdentidad(entrada, sub);
    programar(entrada);
    // Al reactivarse se muestra lo que haya en la cola (nada si se reinició por la pausa).
    if (seActiva) sub.set(estadoPara(entrada, sub));
  }, [activo, intervaloMs, ventana, identidad]);

  // `identidad`: la que declaró el servidor en la última respuesta aplicada (null = desconocida).
  return { vivo: estado.vivo, sinAcceso: estado.sinAcceso, identidad: estado.identidad ?? null };
}

// Sólo para pruebas: estado del registro (cuántas camas tienen lectura programada).
export function _registroParaPruebas() {
  return [...registro.values()].map((e) => ({ camaId: e.camaId, subs: e.subs.size, intervalo: e.intervalo }));
}
